import type { LucideIcon } from "lucide-react";
import {
    BarChart3,
    BookOpen,
    Braces,
    Brain,
    Briefcase,
    Cake,
    DollarSign,
    Dumbbell,
    FlaskConical,
    Flower2,
    Folder,
    Globe,
    Globe2,
    GraduationCap,
    Heart,
    Music,
    Notebook,
    Paintbrush,
    Palette,
    PawPrint,
    PenTool,
    Pencil,
    Plane,
    Scale,
    Sprout,
    Star,
    Stethoscope,
    Terminal,
    Weight,
    Wrench,
} from "lucide-react";

export const WORKSPACE_AVATAR_COLORS = [
    { id: "black", value: "#171717" },
    { id: "red", value: "#E03E3E" },
    { id: "orange", value: "#D9730D" },
    { id: "yellow", value: "#DFAB01" },
    { id: "green", value: "#0F7B6C" },
    { id: "blue", value: "#0B6E99" },
    { id: "purple", value: "#6940A5" },
    { id: "pink", value: "#AD1A72" },
] as const;

export type WorkspaceAvatarColorId = (typeof WORKSPACE_AVATAR_COLORS)[number]["id"];

export const WORKSPACE_AVATAR_ICONS = [
    { id: "folder", Icon: Folder },
    { id: "dollar-sign", Icon: DollarSign },
    { id: "book", Icon: BookOpen },
    { id: "graduation-cap", Icon: GraduationCap },
    { id: "pencil", Icon: Pencil },
    { id: "pen", Icon: PenTool },
    { id: "braces", Icon: Braces },
    { id: "terminal", Icon: Terminal },
    { id: "music", Icon: Music },
    { id: "cake", Icon: Cake },
    { id: "paintbrush", Icon: Paintbrush },
    { id: "palette", Icon: Palette },
    { id: "stethoscope", Icon: Stethoscope },
    { id: "star", Icon: Star },
    { id: "flower", Icon: Flower2 },
    { id: "briefcase", Icon: Briefcase },
    { id: "bar-chart", Icon: BarChart3 },
    { id: "weight", Icon: Weight },
    { id: "dumbbell", Icon: Dumbbell },
    { id: "notebook", Icon: Notebook },
    { id: "scale", Icon: Scale },
    { id: "globe", Icon: Globe },
    { id: "plane", Icon: Plane },
    { id: "globe-2", Icon: Globe2 },
    { id: "wrench", Icon: Wrench },
    { id: "paw", Icon: PawPrint },
    { id: "flask", Icon: FlaskConical },
    { id: "brain", Icon: Brain },
    { id: "heart", Icon: Heart },
    { id: "plant", Icon: Sprout },
] as const;

export type WorkspaceAvatarIconId = (typeof WORKSPACE_AVATAR_ICONS)[number]["id"];

export type WorkspaceAvatarSelection =
    | { mode: "image"; preview: string | null; file: File | null }
    | { mode: "icon"; iconKey: WorkspaceAvatarIconId; color: string };

export const DEFAULT_WORKSPACE_AVATAR: Extract<WorkspaceAvatarSelection, { mode: "icon" }> = {
    mode: "icon",
    iconKey: "folder",
    color: "#171717",
};

/** สไตล์ default ตาม UI (พื้นเทาอ่อน + ไอคอน folder เส้นดำ) */
export const DEFAULT_WORKSPACE_ICON_SURFACE = "rgba(23, 23, 23, 0.094)";

export function getWorkspaceIconDef(iconKey: string): (typeof WORKSPACE_AVATAR_ICONS)[number] | null {
    return WORKSPACE_AVATAR_ICONS.find((i) => i.id === iconKey) ?? null;
}

export function resolveWorkspaceIconComponent(iconKey: string): LucideIcon {
    return getWorkspaceIconDef(iconKey)?.Icon ?? Folder;
}

export function parseWorkspaceAvatarFromTags(
    tags: Array<{ key?: string; value?: string }> | null | undefined,
): Extract<WorkspaceAvatarSelection, { mode: "icon" }> | null {
    if (!tags?.length) return null;
    const map = new Map(tags.map((t) => [String(t.key ?? ""), String(t.value ?? "")]));
    const avatarType = map.get("avatar_type");
    if (avatarType !== "icon") return null;
    const iconKey = map.get("icon_key");
    const color = map.get("icon_color");
    if (!iconKey || !color) return null;
    const def = getWorkspaceIconDef(iconKey);
    if (!def) return null;
    return { mode: "icon", iconKey: def.id, color };
}

export function resolveWorkspaceImageUrl(url?: string | null): string | null {
    if (!url?.trim()) return null;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;

    const base = (process.env.NEXT_PUBLIC_API_BASE_URL ?? "").replace(/\/+$/, "");
    if (!base) return null;

    if (url.startsWith("/")) return `${base}${url}`;
    return `${base}/${url}`;
}

function getWorkspaceImageAllowedOrigins(): string[] {
    return [
        process.env.NEXT_PUBLIC_API_BASE_URL,
        process.env.NEXT_PUBLIC_PRODUCT_API_URL,
        process.env.NEXT_PUBLIC_TRANSACTIONS_API_BASE_URL,
        process.env.NEXT_PUBLIC_CHAT_API_URL,
        process.env.NEXT_PUBLIC_CREDENTIAL_API_URL,
    ]
        .map((base) => {
            const trimmed = (base ?? "").trim().replace(/\/+$/, "");
            if (!trimmed) return null;
            try {
                return new URL(trimmed).origin;
            } catch {
                return null;
            }
        })
        .filter((origin): origin is string => Boolean(origin));
}

function isHireAeraUploadUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        if (!parsed.pathname.startsWith("/uploads/")) return false;
        return (
            parsed.hostname === "hireaera.com" ||
            parsed.hostname.endsWith(".hireaera.com")
        );
    } catch {
        return false;
    }
}

function isAllowedWorkspaceImageUrl(url: string): boolean {
    try {
        return (
            getWorkspaceImageAllowedOrigins().includes(new URL(url).origin) ||
            isHireAeraUploadUrl(url)
        );
    } catch {
        return false;
    }
}

/** Browser display URL — proxy API uploads with server-side auth. */
export function resolveWorkspaceImageDisplayUrl(url?: string | null): string | null {
    const absolute = resolveWorkspaceImageUrl(url);
    if (!absolute) return null;
    if (absolute.startsWith("blob:") || absolute.startsWith("data:")) {
        return absolute;
    }
    if (!isAllowedWorkspaceImageUrl(absolute)) return absolute;
    return `/api/workspace-image?url=${encodeURIComponent(absolute)}`;
}

export function unwrapWorkspaceResponse<T extends Record<string, unknown>>(
    data: unknown,
): T | null {
    if (!data || typeof data !== "object") return null;
    const maybe = data as { data?: unknown; workspace?: unknown };
    const inner = maybe.data ?? maybe.workspace ?? data;
    if (!inner || typeof inner !== "object") return null;
    return inner as T;
}

function readWorkspaceImageRaw(workspace: Record<string, unknown>): string | null {
    const candidates = [
        workspace.imageUrl,
        workspace.image_url,
        workspace.image,
        workspace.thumbnail,
        workspace.thumbnailUrl,
        workspace.thumbnail_url,
    ];
    for (const candidate of candidates) {
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }
    }
    return null;
}

export function normalizeWorkspace<T extends Record<string, unknown>>(
    workspace: T,
): T & { imageUrl?: string | null } {
    return {
        ...workspace,
        imageUrl: readWorkspaceImageRaw(workspace),
    };
}

export function isWorkspaceAvatarImageUrl(url?: string | null): boolean {
    if (!url?.trim()) return false;
    return !url.startsWith("aera-icon://");
}

export function draftToPreviewSelection(draft: {
    tab: "icon" | "image";
    iconKey: WorkspaceAvatarIconId;
    color: string;
    imagePreview: string | null;
    imageFile: File | null;
}): WorkspaceAvatarSelection {
    if (draft.tab === "image" && draft.imagePreview?.trim()) {
        return {
            mode: "image",
            preview: draft.imagePreview,
            file: draft.imageFile,
        };
    }
    return { mode: "icon", iconKey: draft.iconKey, color: draft.color };
}

/** imageUrl สำหรับ POST create — ไม่ส่งฟิลด์เมื่อใช้ไอคอนหรือยังไม่มี URL (API ไม่รับ null) */
export function resolveWorkspaceImageUrlForCreate(
    avatar: WorkspaceAvatarSelection,
    uploadedUrl?: string | null,
): string | undefined {
    if (avatar.mode !== "image") return undefined;
    const url = uploadedUrl?.trim();
    return url || undefined;
}

/** imageUrl สำหรับ PATCH — ส่ง null เพื่อล้างรูปเมื่อเปลี่ยนเป็นไอคอน (undefined จะถูกตัดออกจาก JSON) */
export function resolveWorkspaceImageUrlForSave(
    avatar: WorkspaceAvatarSelection,
    options: { existingUrl?: string | null; uploadedUrl?: string | null } = {},
): string | null {
    if (avatar.mode === "icon") return null;
    if (options.uploadedUrl) return options.uploadedUrl;
    if (avatar.mode === "image") return options.existingUrl ?? null;
    return options.existingUrl ?? null;
}

export function mergeTagsForAvatarSync(
    businessTags: Array<{ id?: string; key: string; value: string }>,
    workspaceTags?: Array<{ id?: string; key?: string; value?: string }> | null,
): Array<{ id?: string; key: string; value: string }> {
    const avatarKeys = new Set(["avatar_type", "icon_key", "icon_color"]);
    const merged = [...businessTags];
    for (const t of workspaceTags ?? []) {
        const key = String(t.key ?? "").trim();
        if (!avatarKeys.has(key)) continue;
        if (merged.some((m) => m.key === key)) continue;
        merged.push({
            id: typeof t.id === "string" ? t.id : undefined,
            key,
            value: String(t.value ?? "").trim(),
        });
    }
    return merged;
}

export function resolveWorkspaceAvatarFromWorkspace(input: {
    imageUrl?: string | null;
    tags?: Array<{ key?: string; value?: string }> | null;
    resolveImageUrl?: (url: string | null | undefined) => string | null;
}): WorkspaceAvatarSelection {
    const resolved = input.resolveImageUrl
        ? input.resolveImageUrl(input.imageUrl ?? null)
        : input.imageUrl ?? null;

    if (isWorkspaceAvatarImageUrl(input.imageUrl) && resolved?.trim()) {
        return { mode: "image", preview: resolved, file: null };
    }

    const fromTags = parseWorkspaceAvatarFromTags(input.tags);
    if (fromTags) return fromTags;

    return DEFAULT_WORKSPACE_AVATAR;
}
